import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import assignmentRuleService from '../api/crm/assignment-rules/assignmentRuleService';
import { industryService, leadSourceService } from '../api/crm/config/configService';

/**
 * Hook for fetching active industries
 */
export const useActiveIndustries = (enabled = true) => {
    return useQuery({
        queryKey: ['industries', 'active'],
        queryFn: async () => {
            const result = await industryService.getAll({ isActive: true });
            if (!result.success) throw new Error(result.error || 'Failed to fetch industries');
            return (result.data || []).map(i => ({
                value: i.name,
                label: i.name
            }));
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook for fetching active lead sources
 */
export const useActiveLeadSources = (enabled = true) => {
    return useQuery({
        queryKey: ['lead-sources', 'active'],
        queryFn: async () => {
            const result = await leadSourceService.getAll({ isActive: true });
            if (!result.success) throw new Error(result.error || 'Failed to fetch lead sources');
            return (result.data || []).map(s => ({
                value: s.name,
                label: s.name
            }));
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook for assignment rule mutations
 */
export const useAssignmentRuleMutations = () => {
    const queryClient = useQueryClient();

    const createRuleMutation = useMutation({
        mutationFn: (ruleData) => assignmentRuleService.createRule(ruleData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assignment-rules'] });
        },
    });

    const updateRuleMutation = useMutation({
        mutationFn: ({ ruleId, ruleData }) => assignmentRuleService.updateRule(ruleId, ruleData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assignment-rules'] });
        },
    });

    return {
        createRule: createRuleMutation,
        updateRule: updateRuleMutation,
    };
};
